import { Client, BannerSet, BannerSize, DataFeed, User } from '../types';
import { v4 as uuidv4 } from 'uuid';

class DataService {
  private mockClients: Client[] = [];
  private mockBannerSets: { [clientId: string]: BannerSet[] } = {};

  constructor() {
    // Initialize with empty arrays
    this.mockClients = [];
    this.mockBannerSets = {};
  }

  private async handleMockRequest(endpoint: string, method: string, data?: any): Promise<any> {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 500));

    // Handle different endpoints
    if (endpoint.startsWith('/clients')) {
      if (endpoint === '/clients') {
        if (method === 'GET') {
          return this.mockClients;
        } else if (method === 'POST') {
          const newClient: Client = {
            ...data,
            id: uuidv4(),
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            bannerSets: []
          };
          this.mockClients.push(newClient);
          this.mockBannerSets[newClient.id] = [];
          return newClient;
        }
      } else {
        const clientId = endpoint.split('/')[2];
        if (endpoint.endsWith('/banner-sets')) {
          if (method === 'GET') {
            return this.mockBannerSets[clientId] || [];
          } else if (method === 'POST') {
            const newBannerSet: BannerSet = {
              ...data,
              id: uuidv4(),
              createdAt: new Date().toISOString(),
              lastModified: new Date().toISOString(),
              sizes: []
            };
            if (!this.mockBannerSets[clientId]) {
              this.mockBannerSets[clientId] = [];
            }
            this.mockBannerSets[clientId].push(newBannerSet);
            return newBannerSet;
          }
        } else if (endpoint.includes('/banner-sets/')) {
          const bannerSetId = endpoint.split('/').pop();
          const bannerSets = this.mockBannerSets[clientId] || [];
          const bannerSetIndex = bannerSets.findIndex(bs => bs.id === bannerSetId);
          
          if (method === 'GET') {
            return bannerSets[bannerSetIndex];
          } else if (method === 'PUT') {
            bannerSets[bannerSetIndex] = {
              ...bannerSets[bannerSetIndex],
              ...data,
              lastModified: new Date().toISOString()
            };
            return bannerSets[bannerSetIndex];
          } else if (method === 'DELETE') {
            bannerSets.splice(bannerSetIndex, 1);
            return { success: true };
          }
        } else {
          const clientIndex = this.mockClients.findIndex(c => c.id === clientId);
          if (method === 'GET') {
            return this.mockClients[clientIndex];
          } else if (method === 'PUT') {
            this.mockClients[clientIndex] = {
              ...this.mockClients[clientIndex],
              ...data,
              updatedAt: new Date().toISOString()
            };
            return this.mockClients[clientIndex];
          }
        }
      }
    }

    throw new Error(`Unhandled mock request: ${method} ${endpoint}`);
  }

  async request(endpoint: string, method: string = 'GET', data?: any): Promise<any> {
    return this.handleMockRequest(endpoint, method, data);
  }

  // Client operations
  async getClients(): Promise<Client[]> {
    return this.mockClients;
  }

  async getClient(id: string): Promise<Client> {
    const client = this.mockClients.find(c => c.id === id);
    if (!client) {
      throw new Error('Client not found');
    }
    return client;
  }

  async createClient(client: Client): Promise<Client> {
    const newClient = {
      ...client,
      id: `client-${Date.now()}`,
      isActive: true,
      bannerSets: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    this.mockClients.push(newClient);
    return newClient;
  }

  async updateClient(id: string, updates: Partial<Client>): Promise<Client> {
    const index = this.mockClients.findIndex(c => c.id === id);
    if (index === -1) {
      throw new Error('Client not found');
    }
    const updatedClient = {
      ...this.mockClients[index],
      ...updates,
      updatedAt: new Date().toISOString()
    };
    this.mockClients[index] = updatedClient;
    return updatedClient;
  }

  async deleteClient(id: string): Promise<void> {
    const index = this.mockClients.findIndex(c => c.id === id);
    if (index === -1) {
      throw new Error('Client not found');
    }
    this.mockClients.splice(index, 1);
  }

  // Banner set operations
  async getBannerSets(clientId: string): Promise<BannerSet[]> {
    const client = await this.getClient(clientId);
    return client.bannerSets || [];
  }

  async getBannerSet(clientId: string, bannerSetId: string): Promise<BannerSet> {
    const client = await this.getClient(clientId);
    const bannerSet = client.bannerSets.find(bs => bs.id === bannerSetId);
    if (!bannerSet) {
      throw new Error('Banner set not found');
    }
    return bannerSet;
  }

  async createBannerSet(clientId: string, bannerSet: BannerSet): Promise<BannerSet> {
    const client = await this.getClient(clientId);
    const newBannerSet = {
      ...bannerSet,
      id: `banner-set-${Date.now()}`,
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString(),
      sizes: []
    };
    if (!client.bannerSets) {
      client.bannerSets = [];
    }
    client.bannerSets.push(newBannerSet);
    return newBannerSet;
  }

  async updateBannerSet(clientId: string, bannerSetId: string, updates: Partial<BannerSet>): Promise<BannerSet> {
    const client = await this.getClient(clientId);
    const index = client.bannerSets.findIndex(bs => bs.id === bannerSetId);
    if (index === -1) {
      throw new Error('Banner set not found');
    }
    const updatedBannerSet = {
      ...client.bannerSets[index],
      ...updates,
      lastModified: new Date().toISOString()
    };
    client.bannerSets[index] = updatedBannerSet;
    return updatedBannerSet;
  }

  async deleteBannerSet(clientId: string, bannerSetId: string): Promise<void> {
    const client = await this.getClient(clientId);
    const index = client.bannerSets.findIndex(bs => bs.id === bannerSetId);
    if (index === -1) {
      throw new Error('Banner set not found');
    }
    client.bannerSets.splice(index, 1);
  }

  // Authentication methods
  async login(email: string, password: string): Promise<User> {
    // Mock login - in a real app, this would validate credentials
    return {
      id: 'user-1',
      name: 'Admin User',
      email,
      role: 'admin',
      permissions: {
        clients: ['read', 'write', 'delete'],
        bannerSets: ['read', 'write', 'delete'],
        users: ['read', 'write', 'delete'],
        settings: ['read', 'write']
      },
      createdAt: new Date().toISOString(),
      lastLogin: new Date().toISOString()
    };
  }

  async logout(): Promise<void> {
    // Mock logout - in a real app, this would clear session
  }

  async getCurrentUser(): Promise<User | null> {
    // Mock get current user - in a real app, this would check session
    return {
      id: 'user-1',
      name: 'Admin User',
      email: 'admin@example.com',
      role: 'admin',
      permissions: {
        clients: ['read', 'write', 'delete'],
        bannerSets: ['read', 'write', 'delete'],
        users: ['read', 'write', 'delete'],
        settings: ['read', 'write']
      },
      createdAt: new Date().toISOString(),
      lastLogin: new Date().toISOString()
    };
  }
}

export const dataService = new DataService(); 